import { useRef, useState } from 'react';

import Modal from '../../components/modal_dialog';

/**
 * Creates a card line item from an unmatched statement line.
 *
 * The form opens pre-populated from the statement line and stays editable,
 * because extracted amounts and merchant names are exactly the values a clerk
 * needs to correct before committing a financial record. The card is never
 * chosen here — the API derives it from the statement's routed card.
 *
 * @param {object} props
 * @param {object} props.data - MobX data store containing the translator.
 * @param {object} props.item - The unmatched statement line being resolved.
 * @param {boolean} props.saving - True while the create request is in flight.
 * @param {string|null} props.error - Message to show when creation failed.
 * @param {Function} props.onConfirm - Called with the edited line-item values.
 * @param {Function} props.onClose - Closes the form. Dismissing with edits in
 *   progress asks the clerk to confirm before the values are thrown away.
 * @returns {JSX.Element} The line-item creation form.
 */
const CreateCardLineItemModal = ({ data, item, saving = false, error = null, onConfirm, onClose }) => {
  const initialForm = {
    amount: item.amount ?? '',
    transaction_date: item.transaction_date || item.posted_date || '',
    posted_date: item.posted_date || item.transaction_date || '',
    description: item.description ?? '',
    currency: item.currency ?? '',
  };

  const [form, setForm] = useState(initialForm);
  // The values the form opened with, so a dismissal can tell an untouched form
  // from one carrying corrections the clerk has not committed yet.
  const pristineForm = useRef(initialForm);

  // Read the value before the deferred updater runs: React may recycle the
  // synthetic event before setForm's callback is invoked.
  const update = (key) => (event) => {
    const { value } = event.target;

    setForm((current) => ({ ...current, [key]: value }));
  };

  const fields = [
    {
      key: 'description',
      label: data.t.read('cc_statements.statement_reconciliation.line_item.merchant'),
      type: 'text',
    },
    {
      key: 'amount',
      label: data.t.read('cc_statements.statement_reconciliation.line_item.amount'),
      type: 'number',
      step: '0.01',
    },
    {
      key: 'transaction_date',
      label: data.t.read('cc_statements.statement_reconciliation.line_item.transaction_date'),
      type: 'date',
    },
    {
      key: 'posted_date',
      label: data.t.read('cc_statements.statement_reconciliation.line_item.posted_date'),
      type: 'date',
    },
  ];

  const incomplete = !form.description.trim() || form.amount === '' || !form.transaction_date;

  const isDirty = Object.keys(pristineForm.current).some(
      (key) => String(form[key]) !== String(pristineForm.current[key])
  );

  return (
    <Modal
      title={data.t.read('cc_statements.statement_reconciliation.create_line_item')}
      subtitle={data.t.read('cc_statements.statement_reconciliation.line_item.description')}
      size='md'
      isDirty={isDirty}
      cancelAction={onClose}
      cancelText={data.t.read('cancel')}
      okAction={() => {
        onConfirm(form);
        // Keep the form open so a failure can be corrected without retyping.
        return false;
      }}
      okText={data.t.read('cc_statements.statement_reconciliation.line_item.create')}
      invalid={incomplete || saving}
      noProcessingLoading
    >
      {fields.map((field) => (
        <div className='form-group' key={field.key}>
          <label htmlFor={`line-item-${field.key}`}>{field.label}</label>
          <input
            id={`line-item-${field.key}`}
            className='form-control'
            type={field.type}
            step={field.step}
            value={form[field.key]}
            onChange={update(field.key)}
          />
        </div>
      ))}

      {error && <div className='alert alert-danger' role='alert'>{error}</div>}
    </Modal>
  );
};

export default CreateCardLineItemModal;
